import { call, put, select } from 'redux-saga/effects';
import { SLOTS } from 'types';
import { ToastTypes } from 'types/toast';
import { ActionType } from 'typesafe-actions';
import { betMessages } from 'constants/toastMessages';
import { tableBetLimitSelector } from 'core/widgets/TableSettings';
import { TableLimitsType } from 'core/widgets/TableSettings/types';
import { addToastAction } from 'core/widgets/Toast/actions';
import { balanceSelector } from 'core/widgets/User';
import { preciseAddSubtract } from 'utils/roundToFix';
import { placeBetAction } from '../actions';
import { availableBetsSelector } from '../BetAvailability';
import { placedBetSelector } from '../selectors';
import { BetType, IBets } from '../types';
import { getBetSum } from '../utils';
import { updateBetsSaga } from './createPendingBetSaga';

export function* placeBetSaga({ payload: newBet }: ActionType<typeof placeBetAction>) {
  const balance: number | null = yield select(balanceSelector);
  const tableLimits: TableLimitsType | undefined = yield select(tableBetLimitSelector);
  const currentBets: IBets = yield select(placedBetSelector);
  const availableBets: SLOTS[] = yield select(availableBetsSelector);

  if (!balance || !tableLimits) {
    return;
  }

  if (availableBets.length > 0 && !availableBets.includes(newBet.slot)) {
    yield put(addToastAction({ type: ToastTypes.ERROR, value: betMessages.BET_NOT_AVAILABLE }));
    return;
  }

  const { past, present } = currentBets;

  const placedBetSumBeforeNewBet: number = getBetSum(present);

  if (placedBetSumBeforeNewBet >= tableLimits.total.max) {
    yield put(addToastAction({ type: ToastTypes.ERROR, value: betMessages.MAX_BET_REACHED }));
    return;
  }

  const isAlreadyMaxLimitReached = present[newBet.slot] >= tableLimits[newBet.slot].max;

  if (isAlreadyMaxLimitReached) {
    yield put(addToastAction({ type: ToastTypes.ERROR, value: betMessages.MAX_BET_REACHED }));
    return;
  }

  const remainingBalance: number = preciseAddSubtract(
    balance,
    placedBetSumBeforeNewBet,
    'subtract',
  );

  if (remainingBalance <= 0) {
    yield put(addToastAction({ type: ToastTypes.ERROR, value: betMessages.LOW_BALANCE }));
    return;
  }

  const currentBetValue = present[newBet.slot] || 0;
  const newPlacedBetSum: number = preciseAddSubtract(currentBetValue, newBet.value, 'add');
  const betTypeLimit = tableLimits[newBet.slot];

  if (currentBetValue === 0 && remainingBalance < betTypeLimit.min) {
    yield put(addToastAction({ type: ToastTypes.ERROR, value: betMessages.LOW_BALANCE }));
    return;
  }

  const maxAffordableForSlot = preciseAddSubtract(currentBetValue, remainingBalance, 'add');

  const affordableAmount = Math.min(newPlacedBetSum, maxAffordableForSlot);
  const slotLimitedAmount = Math.min(affordableAmount, betTypeLimit.max);

  let finalBetValue: number;
  if (newPlacedBetSum >= betTypeLimit.min && newPlacedBetSum <= betTypeLimit.max) {
    finalBetValue = affordableAmount;
  } else if (newPlacedBetSum < betTypeLimit.min && maxAffordableForSlot >= betTypeLimit.min) {
    finalBetValue = betTypeLimit.min;
    yield put(addToastAction({ type: ToastTypes.DEFAULT, value: betMessages.MIN_BET_REACHED }));
  } else {
    finalBetValue = slotLimitedAmount;
    yield put(addToastAction({ type: ToastTypes.ERROR, value: betMessages.MAX_BET_REACHED }));
  }

  const updatedPresent: BetType = {
    ...present,
    [newBet.slot]: finalBetValue,
  };

  const totalBetsSum = getBetSum(updatedPresent);

  if (totalBetsSum > tableLimits.total.max) {
    yield put(addToastAction({ type: ToastTypes.ERROR, value: betMessages.MAX_BET_REACHED }));
    return;
  }

  if (totalBetsSum > balance) {
    yield put(addToastAction({ type: ToastTypes.ERROR, value: betMessages.LOW_BALANCE }));
    return;
  }

  const isPresentEmpty = Object.values(present).every((value) => value === 0);
  const updatedPast: BetType[] = isPresentEmpty ? past : [...past, present];

  yield call(updateBetsSaga, { past: updatedPast, present: updatedPresent });
}
